import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Search, Plus, RefreshCw, ChevronRight, Building2, Users, Layers,
  Database, Settings2, ScrollText, CheckCircle2, AlertTriangle, XCircle,
  Pencil, Trash2, Eye, EyeOff, ToggleLeft, ToggleRight, X,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import { Drawer } from '@/components/primitives/Drawer'

// ── Types ─────────────────────────────────────────────────────────────────────

type Tab = 'subscriptions' | 'modules' | 'users' | 'database-users' | 'companies' | 'sync-config' | 'sync-log'

interface Subscription {
  id: string
  organizationName: string
  serverName: string | null
  dbName: string | null
  subscriberType: 'Host' | 'Tenant'
  authenticationType: string | null
  isActive: boolean
  companyCount: number
  moduleCount: number
  customerId: number | null
  customerName: string | null
  tenantId: string | null
  serverUsername: string | null
  hasDbPassword: boolean
}

interface SubEnabledModule {
  id: number
  name: string
  displayName: string
  schemaName: string | null
  isSyncActive: boolean
}

interface SubDetail {
  enabledModules: SubEnabledModule[]
}

interface CModule {
  id: number
  name: string
  displayName: string
  urlBase: string | null
  schemaName: string | null
  isStatic: boolean
  isActive: boolean
  ordinal: number
}

interface CUser {
  id: number
  subscriptionId: string
  subscriptionName: string
  email: string
  firstName: string | null
  lastName: string | null
  displayName: string | null
  entraUpn: string | null
  isActive: boolean
  enableErrorAlert: boolean
}

interface DbUser {
  id: number
  subscriptionId: string
  subscriptionName: string
  userType: string
  allowedScopes: string | null
  allowedIps: string | null
  dbUserId: string | null
  hasPassword: boolean
}

interface SyncEntry {
  id: number
  subscriptionId: string
  subscriptionName: string
  companyId: number | null
  companyName: string | null
  source: string
  startedAt: string | null
  completedAt: string | null
  runStatus: string | null
  hasError: boolean
  errorMessage: string | null
  isFullSync: boolean
  tryCount: number
}

interface SyncConfig {
  id: string
  subscriptionId: string
  subscriptionName: string
  companyId: number | null
  companyName: string | null
  featureName: string
  isSyncFromFortnox: boolean
  isEnableSync: boolean
}

interface Company {
  id: number
  organizationNumber: string | null
  name: string
  isActive: boolean
  subscriptionId: string
  subscriptionName: string
  modules: string[]
}

interface AvailableCustomer {
  id: number
  name: string
  orgNr: string | null
  tenantId: string | null
  isOnboarded: boolean
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function relTime(iso: string | null): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 2)  return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function Badge({ label, color }: { label: string; color?: string }) {
  const c = color ?? 'var(--ns-text-muted)'
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 600, letterSpacing: '0.03em',
      padding: '2px 7px', borderRadius: 4,
      background: `color-mix(in oklch, ${c} 15%, transparent)`,
      color: c,
      border: `1px solid color-mix(in oklch, ${c} 30%, transparent)`,
    }}>
      {label}
    </span>
  )
}

function StatusDot({ ok: isOk }: { ok: boolean }) {
  return isOk
    ? <CheckCircle2 size={13} style={{ color: 'oklch(0.66 0.13 150)' }} />
    : <XCircle      size={13} style={{ color: 'var(--ns-err)' }} />
}

function TabBtn({ tab, active, label, icon: Icon, count }: {
  tab: Tab; active: Tab; label: string; icon: React.ElementType; count?: number
}) {
  return (
    <button style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 500,
      background: active === tab ? 'var(--ns-surface-2)' : 'transparent',
      color: active === tab ? 'var(--ns-text)' : 'var(--ns-text-muted)',
      border: active === tab ? '1px solid var(--ns-border)' : '1px solid transparent',
      cursor: 'pointer',
    }}>
      <Icon size={14} />
      {label}
      {count !== undefined && (
        <span style={{
          fontSize: 10.5, fontWeight: 700, background: 'var(--ns-border)',
          borderRadius: 10, padding: '1px 6px', color: 'var(--ns-text-secondary)',
        }}>{count}</span>
      )}
    </button>
  )
}

function TH({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th style={{
      padding: '8px 14px', textAlign: right ? 'right' : 'left',
      fontSize: 11, fontWeight: 600, color: 'var(--ns-text-muted)',
      textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>
      {children}
    </th>
  )
}

function TD({ children, mono, muted, right }: { children: React.ReactNode; mono?: boolean; muted?: boolean; right?: boolean }) {
  return (
    <td style={{
      padding: '10px 14px', textAlign: right ? 'right' : 'left',
      fontFamily: mono ? 'var(--font-mono)' : undefined,
      fontSize: mono ? 12 : 13,
      color: muted ? 'var(--ns-text-muted)' : undefined,
    }}>
      {children}
    </td>
  )
}

function EmptyRow({ cols, msg }: { cols: number; msg: string }) {
  return (
    <tr>
      <td colSpan={cols} style={{ padding: 40, textAlign: 'center', color: 'var(--ns-text-muted)', fontSize: 13 }}>
        {msg}
      </td>
    </tr>
  )
}

// ── Onboard drawer ────────────────────────────────────────────────────────────

function OnboardDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [search, setSearch]           = useState('')
  const [selected, setSelected]       = useState<AvailableCustomer | null>(null)
  const [serverName, setServerName]   = useState('')
  const [dbName, setDbName]           = useState('')
  const subType = 'Tenant'
  const [orgName, setOrgName]         = useState('')

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ['clementine', 'customers'],
    queryFn:  () => api.get<AvailableCustomer[]>('/clementine/customers'),
    enabled:  open,
  })

  const filtered = useMemo(() =>
    customers.filter(c =>
      !c.isOnboarded &&
      c.name.toLowerCase().includes(search.toLowerCase())
    ), [customers, search])

  const create = useMutation({
    mutationFn: () => api.post('/clementine/subscriptions', {
      customerId:      selected?.id,
      organizationName: orgName || selected?.name,
      serverName:      serverName || null,
      dbName:          dbName || null,
      subscriberType:  subType,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clementine', 'subscriptions'] })
      qc.invalidateQueries({ queryKey: ['clementine', 'customers'] })
      toast.success(`${orgName || selected?.name} onboarded to Clementine`)
      handleClose()
    },
    onError: (e) => { console.error('Onboard error:', e); toast.error(`Failed: ${e instanceof Error ? e.message : String(e)}`) },
  })

  const handleClose = () => {
    setSearch(''); setSelected(null)
    setServerName(''); setDbName('')
    setOrgName('')
    onClose()
  }

  const handleSelect = (c: AvailableCustomer) => {
    setSelected(c)
    setOrgName(c.name)
    setSearch('')
  }

  return (
    <Drawer open={open} onClose={handleClose}>
      <div className="drawer-head">
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Onboard customer to Clementine</div>
          <div style={{ fontSize: 12, color: 'var(--ns-text-muted)', marginTop: 2 }}>
            Select an existing customer and configure their Clementine subscription
          </div>
        </div>
        <button className="btn btn-ghost btn-icon" onClick={handleClose}><X size={15} /></button>
      </div>

      <div className="drawer-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Step 1 — pick customer */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
            Customer
          </div>

          {selected ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', border: '1px solid var(--ns-brand-strong)', borderRadius: 8, background: 'color-mix(in oklch, var(--ns-brand-strong) 8%, transparent)' }}>
              <div>
                <div style={{ fontWeight: 500, fontSize: 13 }}>{selected.name}</div>
                {selected.orgNr && <div style={{ fontSize: 11, color: 'var(--ns-text-muted)', fontFamily: 'var(--font-mono)' }}>{selected.orgNr}</div>}
              </div>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setSelected(null)}><X size={13} /></button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ position: 'relative' }}>
                <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--ns-text-muted)' }} />
                <input
                  className="input"
                  style={{ paddingLeft: 30 }}
                  placeholder="Search customers…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div style={{ border: '1px solid var(--ns-border)', borderRadius: 8, maxHeight: 220, overflow: 'auto' }}>
                {isLoading && (
                  <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--ns-text-muted)' }}>Loading…</div>
                )}
                {!isLoading && filtered.length === 0 && (
                  <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--ns-text-muted)' }}>
                    {search ? 'No matching customers' : 'All customers already onboarded'}
                  </div>
                )}
                {filtered.map((c, i) => (
                  <button
                    key={c.id}
                    onClick={() => handleSelect(c)}
                    style={{
                      width: '100%', textAlign: 'left', padding: '9px 12px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      borderTop: i > 0 ? '1px solid var(--ns-border)' : 'none',
                      display: 'flex', flexDirection: 'column', gap: 1,
                    }}
                    className="table-row-hover"
                  >
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</span>
                    {c.orgNr && <span style={{ fontSize: 11, color: 'var(--ns-text-muted)', fontFamily: 'var(--font-mono)' }}>{c.orgNr}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Step 2 — details (only shown after customer selected) */}
        {selected && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Display name
              </label>
              <input className="input" value={orgName} onChange={e => setOrgName(e.target.value)} placeholder={selected.name} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                SQL Server
              </label>
              <input className="input" value={serverName} onChange={e => setServerName(e.target.value)} placeholder="e.g. customer.database.windows.net" style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Database name
              </label>
              <input className="input" value={dbName} onChange={e => setDbName(e.target.value)} placeholder="e.g. clementine" style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
            </div>

            {selected.tenantId && (
              <div style={{ padding: '10px 12px', background: 'var(--ns-surface-2)', borderRadius: 8, border: '1px solid var(--ns-border)' }}>
                <div style={{ fontSize: 11, color: 'var(--ns-text-muted)', marginBottom: 2 }}>Entra Tenant ID (from customer)</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{selected.tenantId}</div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="drawer-foot">
        <button className="btn btn-ghost" onClick={handleClose}>Cancel</button>
        <button
          className="btn btn-primary"
          disabled={!selected || create.isPending}
          onClick={() => create.mutate()}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <Plus size={14} />
          {create.isPending ? 'Onboarding…' : 'Onboard customer'}
        </button>
      </div>
    </Drawer>
  )
}

// ── Subscription detail panel ─────────────────────────────────────────────────

function SubDetailPanel({ sub, allModules }: { sub: Subscription; allModules: CModule[] }) {
  const qc = useQueryClient()

  const { data: detail, isLoading } = useQuery({
    queryKey: ['clementine', 'subscription', sub.id],
    queryFn:  () => api.get<SubDetail>(`/clementine/subscriptions/${sub.id}`),
  })

  const setModules = useMutation({
    mutationFn: (modules: Array<{ moduleId: number; schemaName: string | null; isSyncActive: boolean }>) =>
      api.put(`/clementine/subscriptions/${sub.id}/modules`, { modules }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clementine', 'subscription', sub.id] })
      qc.invalidateQueries({ queryKey: ['clementine', 'subscriptions'] })
      toast.success('Modules updated')
    },
    onError: () => toast.error('Failed to update modules'),
  })

  const enabledIds = new Set(detail?.enabledModules.map(m => m.id) ?? [])
  const activeModules = allModules.filter(m => m.isActive)

  const toggleModule = (m: CModule) => {
    if (!detail || setModules.isPending) return
    const wasEnabled = enabledIds.has(m.id)
    const newList = wasEnabled
      ? detail.enabledModules
          .filter(em => em.id !== m.id)
          .map(em => ({ moduleId: em.id, schemaName: em.schemaName, isSyncActive: em.isSyncActive }))
      : [
          ...detail.enabledModules.map(em => ({ moduleId: em.id, schemaName: em.schemaName, isSyncActive: em.isSyncActive })),
          { moduleId: m.id, schemaName: m.schemaName, isSyncActive: true },
        ]
    setModules.mutate(newList)
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          Connection
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--ns-text-muted)' }}>
          <div>Server: <span style={{ fontFamily: 'var(--font-mono)' }}>{sub.serverName ?? '—'}</span></div>
          <div>Database: <span style={{ fontFamily: 'var(--font-mono)' }}>{sub.dbName ?? '—'}</span></div>
          {sub.customerName && <div style={{ marginTop: 4 }}>Customer: <span style={{ color: 'var(--ns-text)' }}>{sub.customerName}</span></div>}
          <div style={{ marginTop: 8, fontSize: 11 }}>ID</div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{sub.id}</div>
        </div>
      </div>

      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          Enabled modules
        </div>
        {isLoading ? (
          <div style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>Loading…</div>
        ) : activeModules.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>No modules defined</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {activeModules.map(m => {
              const enabled = enabledIds.has(m.id)
              return (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12 }}>{m.displayName}</span>
                  <button
                    onClick={() => toggleModule(m)}
                    disabled={setModules.isPending}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, opacity: setModules.isPending ? 0.5 : 1 }}
                  >
                    {enabled
                      ? <ToggleRight size={18} style={{ color: 'oklch(0.66 0.13 150)' }} />
                      : <ToggleLeft  size={18} style={{ color: 'var(--ns-text-muted)' }} />}
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Subscription edit drawer ──────────────────────────────────────────────────

const AUTH_TYPES = ['SQL Server Authentication', 'Service Principal', 'Azure Active Directory']

type ModuleState = { enabled: boolean; schemaName: string }

function SubEditDrawer({ open, onClose, sub, allModules }: {
  open: boolean; onClose: () => void; sub: Subscription | null; allModules: CModule[]
}) {
  const qc = useQueryClient()

  const [orgName,    setOrgName]    = useState('')
  const [subType,    setSubType]    = useState<'Host' | 'Tenant'>('Tenant')
  const [isActive,   setIsActive]   = useState(true)

  // database mode
  const [dbMode,     setDbMode]     = useState<'internal' | 'external'>('internal')
  const [authType,   setAuthType]   = useState(AUTH_TYPES[0])
  const [tenantId,   setTenantId]   = useState('')
  const [serverName, setServerName] = useState('')
  const [username,   setUsername]   = useState('')
  const [password,   setPassword]   = useState('')
  const [showPw,     setShowPw]     = useState(false)
  const [dbName,     setDbName]     = useState('')
  const [dbList,     setDbList]     = useState<string[]>([])
  const [fetching,   setFetching]   = useState(false)
  const [fetchErr,   setFetchErr]   = useState<string | null>(null)

  // module schema state
  const [moduleStates, setModuleStates] = useState<Record<number, ModuleState>>({})

  const isSP = authType === 'Service Principal'
  const userLabel = isSP ? 'Client ID' : 'Username'
  const pwLabel   = isSP ? 'Client Secret' : 'Password'

  // load sub detail for enabled modules + their schema names
  const { data: subDetail } = useQuery({
    queryKey: ['clementine', 'sub-detail', sub?.id],
    queryFn:  () => api.get<SubDetail>(`/clementine/subscriptions/${sub?.id}`),
    enabled:  !!open && !!sub,
  })

  useEffect(() => {
    if (open && sub) {
      setOrgName(sub.organizationName)
      setSubType(sub.subscriberType)
      setIsActive(sub.isActive)
      const isExternal = !!sub.serverName
      setDbMode(isExternal ? 'external' : 'internal')
      setAuthType(sub.authenticationType ?? AUTH_TYPES[0])
      setTenantId(sub.tenantId ?? '')
      setServerName(sub.serverName ?? '')
      setUsername(sub.serverUsername ?? '')
      setPassword('')
      setShowPw(false)
      setDbName(sub.dbName ?? '')
      setDbList(sub.dbName ? [sub.dbName] : [])
      setFetchErr(null)
    }
  }, [open, sub])

  // init module states once detail loads
  useEffect(() => {
    if (!open || !sub || !subDetail || allModules.length === 0) return
    const states: Record<number, ModuleState> = {}
    for (const m of allModules) {
      const em = subDetail.enabledModules.find(x => x.id === m.id)
      states[m.id] = { enabled: !!em, schemaName: em?.schemaName ?? m.schemaName ?? '' }
    }
    setModuleStates(states)
  }, [open, sub?.id, subDetail, allModules])

  const handleFetchDatabases = async () => {
    setFetchErr(null)
    setFetching(true)
    try {
      const dbs = await api.post<string[]>('/clementine/fetch-databases', {
        serverName, authType, username, password,
      })
      setDbList(dbs)
      if (dbs.length > 0 && !dbName) setDbName(dbs[0])
    } catch (e: unknown) {
      setFetchErr(e instanceof Error ? e.message : 'Connection failed')
    } finally {
      setFetching(false)
    }
  }

  const setModuleEnabled = (id: number, enabled: boolean) =>
    setModuleStates(prev => ({ ...prev, [id]: { ...prev[id], enabled } }))
  const setModuleSchema = (id: number, schemaName: string) =>
    setModuleStates(prev => ({ ...prev, [id]: { ...prev[id], schemaName } }))

  const update = useMutation({
    mutationFn: async () => {
      await api.put(`/clementine/subscriptions/${sub?.id}`, {
        organizationName:   orgName.trim(),
        subscriberType:     subType,
        isActive,
        tenantId:           tenantId.trim() || null,
        serverName:         dbMode === 'external' ? (serverName.trim() || null) : null,
        dbName:             dbMode === 'external' ? (dbName.trim() || null) : null,
        authenticationType: dbMode === 'external' ? authType : null,
        serverUsername:     dbMode === 'external' ? (username.trim() || null) : null,
        dbPassword:         dbMode === 'external' && password.trim() ? password.trim() : undefined,
      })
      const modules = Object.entries(moduleStates)
        .filter(([, s]) => s.enabled)
        .map(([id, s]) => ({ moduleId: parseInt(id), schemaName: s.schemaName.trim() || null, isSyncActive: true }))
      await api.put(`/clementine/subscriptions/${sub?.id}/modules`, { modules })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clementine', 'subscriptions'] })
      qc.invalidateQueries({ queryKey: ['clementine', 'sub-detail', sub?.id] })
      toast.success('Subscription updated')
      onClose()
    },
    onError: () => toast.error('Failed to update subscription'),
  })

  const labelStyle: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  }
  const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 }

  return (
    <Drawer open={open} onClose={onClose}>
      <div className="drawer-head">
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>Edit subscription</div>
          {sub && <div style={{ fontSize: 12, color: 'var(--ns-text-muted)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>{sub.id}</div>}
        </div>
        <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={15} /></button>
      </div>

      <div className="drawer-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Display name */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Display name <span style={{ color: 'var(--ns-err)' }}>*</span></label>
          <input className="input" value={orgName} onChange={e => setOrgName(e.target.value)} />
        </div>

        {/* Subscriber type */}
        <div style={fieldStyle}>
          <label style={labelStyle}>Subscriber type</label>
          <select className="input" value={subType} onChange={e => setSubType(e.target.value as 'Host' | 'Tenant')}>
            <option value="Tenant">Tenant</option>
            <option value="Host">Host</option>
          </select>
        </div>

        {/* Internal / External toggle */}
        <div style={{ display: 'flex', gap: 0, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--ns-border)' }}>
          {(['internal', 'external'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setDbMode(mode)}
              style={{
                flex: 1, padding: '8px 0', fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer',
                background: dbMode === mode ? 'var(--ns-brand)' : 'var(--ns-surface-2)',
                color: dbMode === mode ? '#fff' : 'var(--ns-text-muted)',
              }}
            >
              {mode === 'internal' ? 'Use Internal Database' : 'Use External Database'}
            </button>
          ))}
        </div>

        {/* External database fields */}
        {dbMode === 'external' && (<>
          <div style={fieldStyle}>
            <label style={labelStyle}>Authentication type</label>
            <select className="input" value={authType} onChange={e => setAuthType(e.target.value)}>
              {AUTH_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>

          {isSP && (
            <div style={fieldStyle}>
              <label style={labelStyle}>Tenant ID</label>
              <input
                className="input"
                value={tenantId}
                onChange={e => setTenantId(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
              />
            </div>
          )}

          <div style={fieldStyle}>
            <label style={labelStyle}>Server name</label>
            <input
              className="input"
              value={serverName}
              onChange={e => setServerName(e.target.value)}
              placeholder="e.g. customer.database.windows.net"
              style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ ...fieldStyle, flex: 1 }}>
              <label style={labelStyle}>{userLabel}</label>
              <input className="input" value={username} onChange={e => setUsername(e.target.value)} placeholder={isSP ? 'App client ID' : 'SQL login'} />
            </div>
            <div style={{ ...fieldStyle, flex: 1 }}>
              <label style={labelStyle}>
                {pwLabel}{sub?.hasDbPassword && !password && (
                  <span style={{ color: 'oklch(0.66 0.13 150)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}> (set)</span>
                )}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  className="input"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={sub?.hasDbPassword ? '••••••••' : isSP ? 'Client secret' : 'Password'}
                  style={{ width: '100%', paddingRight: 32 }}
                />
                <button
                  onClick={() => setShowPw(v => !v)}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ns-text-muted)', padding: 0 }}
                >
                  {showPw ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>
          </div>

          {/* Fetch Databases + DB picker */}
          <div style={fieldStyle}>
            <label style={labelStyle}>Database</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {dbList.length > 0 ? (
                <select className="input" style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 12 }} value={dbName} onChange={e => setDbName(e.target.value)}>
                  {dbList.map(d => <option key={d}>{d}</option>)}
                </select>
              ) : (
                <input
                  className="input"
                  style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 12 }}
                  value={dbName}
                  onChange={e => setDbName(e.target.value)}
                  placeholder="Database name"
                />
              )}
              <button
                className="btn btn-ghost"
                style={{ whiteSpace: 'nowrap', fontSize: 12 }}
                disabled={fetching || !serverName.trim() || !username.trim() || !password.trim()}
                onClick={handleFetchDatabases}
              >
                {fetching ? <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Database size={12} />}
                {fetching ? 'Fetching…' : 'Fetch databases'}
              </button>
            </div>
            {fetchErr && <div style={{ fontSize: 11, color: 'var(--ns-err)', marginTop: 4 }}>{fetchErr}</div>}
            <div style={{ fontSize: 11, color: 'var(--ns-text-muted)', marginTop: 2 }}>
              Fill in credentials then click "Fetch databases" to browse, or type the name directly.
            </div>
          </div>
        </>)}

        {/* Module schema assignments */}
        {allModules.length > 0 && (
          <div style={{ borderTop: '1px solid var(--ns-border)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={labelStyle}>Modules</div>
            {allModules.filter(m => m.isActive).map(m => {
              const state = moduleStates[m.id] ?? { enabled: false, schemaName: m.schemaName ?? '' }
              return (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={state.enabled}
                    onChange={e => setModuleEnabled(m.id, e.target.checked)}
                    style={{ flexShrink: 0, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 500, minWidth: 130 }}>{m.displayName}</span>
                  <input
                    className="input"
                    value={state.schemaName}
                    onChange={e => setModuleSchema(m.id, e.target.value)}
                    placeholder={m.schemaName ?? m.name.toLowerCase()}
                    disabled={!state.enabled}
                    style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 12, opacity: state.enabled ? 1 : 0.45 }}
                  />
                </div>
              )
            })}
            <div style={{ fontSize: 11, color: 'var(--ns-text-muted)', marginTop: 2 }}>
              * After renaming the module schema, you will no longer be able to use the old schema.
            </div>
          </div>
        )}

        {/* Active toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--ns-border)' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Active</div>
            <div style={{ fontSize: 11, color: 'var(--ns-text-muted)' }}>Inactive subscriptions are hidden from sync</div>
          </div>
          <button onClick={() => setIsActive(v => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            {isActive
              ? <ToggleRight size={22} style={{ color: 'oklch(0.66 0.13 150)' }} />
              : <ToggleLeft  size={22} style={{ color: 'var(--ns-text-muted)' }} />}
          </button>
        </div>
      </div>

      <div className="drawer-foot">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button
          className="btn btn-primary"
          disabled={!orgName.trim() || update.isPending}
          onClick={() => update.mutate()}
        >
          {update.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </Drawer>
  )
}

// ── Subscriptions tab ─────────────────────────────────────────────────────────

function SubscriptionsTab({ subs, search, modules, onEdit }: {
  subs: Subscription[]; search: string; modules: CModule[]
  onEdit: (s: Subscription) => void
}) {
  const qc = useQueryClient()
  const [expanded,   setExpanded]   = useState<Set<string>>(new Set())
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const filtered = subs.filter(s =>
    s.organizationName.toLowerCase().includes(search.toLowerCase())
  )

  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const deleteSub = useMutation({
    mutationFn: (id: string) => api.delete(`/clementine/subscriptions/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clementine', 'subscriptions'] })
      setDeletingId(null)
      toast.success('Subscription removed')
    },
    onError: () => toast.error('Failed to remove subscription'),
  })

  return (
    <div style={{ border: '1px solid var(--ns-border)', borderRadius: 10, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--ns-border)', background: 'var(--ns-surface-2)' }}>
            <th style={{ width: 24 }} />
            <TH>Subscription</TH>
            <TH>Type</TH>
            <TH>SQL Server</TH>
            <TH>Database</TH>
            <TH>Companies</TH>
            <TH>Modules</TH>
            <TH>Status</TH>
            <TH right>Actions</TH>
          </tr>
        </thead>
        <tbody>
          {filtered.map(s => {
            const exp = expanded.has(s.id)
            return (
              <>
                <tr
                  key={s.id}
                  onClick={() => toggle(s.id)}
                  style={{ cursor: 'pointer', borderTop: '1px solid var(--ns-border)', background: exp ? 'var(--ns-surface-2)' : undefined }}
                  className="table-row-hover"
                >
                  <td style={{ padding: '10px 14px', width: 24 }}>
                    <ChevronRight size={13} style={{ color: 'var(--ns-text-muted)', transform: exp ? 'rotate(90deg)' : undefined, transition: 'transform 150ms' }} />
                  </td>
                  <TD>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--ns-surface-2)', border: '1px solid var(--ns-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Building2 size={13} style={{ color: 'var(--ns-text-muted)' }} />
                      </div>
                      <span style={{ fontWeight: 500 }}>{s.organizationName}</span>
                    </div>
                  </TD>
                  <TD><Badge label={s.subscriberType} color={s.subscriberType === 'Host' ? 'oklch(0.65 0.13 245)' : 'oklch(0.72 0.052 198)'} /></TD>
                  <TD mono muted>{s.serverName ?? '—'}</TD>
                  <TD mono muted>{s.dbName ?? '—'}</TD>
                  <TD mono muted>{s.companyCount}</TD>
                  <TD mono muted>{s.moduleCount}</TD>
                  <TD>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <StatusDot ok={s.isActive} />
                      <span style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>{s.isActive ? 'Active' : 'Inactive'}</span>
                    </div>
                  </TD>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                      <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => onEdit(s)}><Pencil size={12} /></button>
                      {deletingId === s.id ? (
                        <>
                          <span style={{ fontSize: 11, color: 'var(--ns-err)' }}>Remove?</span>
                          <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px', color: 'var(--ns-err)' }} onClick={() => deleteSub.mutate(s.id)} disabled={deleteSub.isPending}>Yes</button>
                          <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => setDeletingId(null)}>No</button>
                        </>
                      ) : (
                        <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px', color: 'var(--ns-err)' }} onClick={() => setDeletingId(s.id)}><Trash2 size={12} /></button>
                      )}
                    </div>
                  </td>
                </tr>
                {exp && (
                  <tr key={`${s.id}-detail`} style={{ background: 'var(--ns-surface-2)', borderTop: '1px solid var(--ns-border)' }}>
                    <td colSpan={9} style={{ padding: '16px 42px' }}>
                      <SubDetailPanel sub={s} allModules={modules} />
                    </td>
                  </tr>
                )}
              </>
            )
          })}
          {filtered.length === 0 && <EmptyRow cols={9} msg="No subscriptions match your search" />}
        </tbody>
      </table>
    </div>
  )
}

// ── Module drawer (add / edit) ────────────────────────────────────────────────

function ModuleDrawer({ open, onClose, module: mod }: { open: boolean; onClose: () => void; module: CModule | null }) {
  const qc     = useQueryClient()
  const isEdit = mod !== null

  const [name,        setName]        = useState('')
  const [displayName, setDisplayName] = useState('')
  const [urlBase,     setUrlBase]     = useState('')
  const [schemaName,  setSchemaName]  = useState('')
  const [ordinal,     setOrdinal]     = useState(0)
  const [isStatic,    setIsStatic]    = useState(true)

  useEffect(() => {
    if (open) {
      setName(mod?.name ?? '')
      setDisplayName(mod?.displayName ?? '')
      setUrlBase(mod?.urlBase ?? '')
      setSchemaName(mod?.schemaName ?? '')
      setOrdinal(mod?.ordinal ?? 0)
      setIsStatic(mod?.isStatic ?? true)
    }
  }, [open, mod])

  const create = useMutation({
    mutationFn: () => api.post('/clementine/modules', {
      name: name.trim(), displayName: displayName.trim(),
      urlBase: urlBase.trim() || null, schemaName: schemaName.trim() || null,
      isStatic, ordinal,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clementine', 'modules'] }); toast.success('Module added'); onClose() },
    onError:   () => toast.error('Failed to add module'),
  })

  const update = useMutation({
    mutationFn: () => api.put(`/clementine/modules/${mod?.id}`, {
      displayName: displayName.trim(),
      urlBase: urlBase.trim() || null, schemaName: schemaName.trim() || null,
      isStatic, isActive: mod?.isActive, ordinal,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clementine', 'modules'] }); toast.success('Module updated'); onClose() },
    onError:   () => toast.error('Failed to update module'),
  })

  const isPending = create.isPending || update.isPending
  const canSave   = displayName.trim() && (isEdit || name.trim())

  return (
    <Drawer open={open} onClose={onClose}>
      <div className="drawer-head">
        <div><div style={{ fontWeight: 600, fontSize: 15 }}>{isEdit ? 'Edit module' : 'Add module'}</div></div>
        <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={15} /></button>
      </div>

      <div className="drawer-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {!isEdit && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Internal name <span style={{ color: 'var(--ns-err)' }}>*</span>
            </label>
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Fortnox" style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Display name <span style={{ color: 'var(--ns-err)' }}>*</span>
          </label>
          <input className="input" value={displayName} onChange={e => setDisplayName(e.target.value)} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Schema name</label>
          <input className="input" value={schemaName} onChange={e => setSchemaName(e.target.value)} placeholder="e.g. fortnox" style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>URL base</label>
          <input className="input" value={urlBase} onChange={e => setUrlBase(e.target.value)} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ordinal</label>
          <input className="input" type="number" value={ordinal} onChange={e => setOrdinal(Number(e.target.value))} style={{ fontFamily: 'var(--font-mono)', fontSize: 12, width: 80 }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--ns-border)' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Built-in</div>
            <div style={{ fontSize: 11, color: 'var(--ns-text-muted)' }}>Off = custom module</div>
          </div>
          <button onClick={() => setIsStatic(v => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            {isStatic
              ? <ToggleRight size={22} style={{ color: 'oklch(0.66 0.13 150)' }} />
              : <ToggleLeft  size={22} style={{ color: 'var(--ns-text-muted)' }} />}
          </button>
        </div>
      </div>

      <div className="drawer-foot">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button
          className="btn btn-primary"
          disabled={!canSave || isPending}
          onClick={() => isEdit ? update.mutate() : create.mutate()}
        >
          {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add module'}
        </button>
      </div>
    </Drawer>
  )
}

// ── Modules tab ───────────────────────────────────────────────────────────────

function ModulesTab({ modules, search, onEdit }: { modules: CModule[]; search: string; onEdit: (m: CModule) => void }) {
  const qc = useQueryClient()
  const toggleActive = useMutation({
    mutationFn: (m: CModule) => api.put(`/clementine/modules/${m.id}`, {
      displayName: m.displayName, urlBase: m.urlBase, schemaName: m.schemaName,
      isStatic: m.isStatic, isActive: !m.isActive, ordinal: m.ordinal,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clementine', 'modules'] }),
    onError:   () => toast.error('Failed to update module'),
  })

  const filtered = modules.filter(m =>
    m.displayName.toLowerCase().includes(search.toLowerCase()) ||
    m.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div style={{ border: '1px solid var(--ns-border)', borderRadius: 10, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--ns-border)', background: 'var(--ns-surface-2)' }}>
            <TH>Module</TH>
            <TH>Internal name</TH>
            <TH>Schema</TH>
            <TH>Type</TH>
            <TH>Order</TH>
            <TH>Active</TH>
            <TH right>Actions</TH>
          </tr>
        </thead>
        <tbody>
          {filtered.map(m => (
            <tr key={m.id} style={{ borderTop: '1px solid var(--ns-border)' }} className="table-row-hover">
              <TD><span style={{ fontWeight: 500 }}>{m.displayName}</span></TD>
              <TD mono muted>{m.name}</TD>
              <TD mono muted>{m.schemaName ?? '—'}</TD>
              <TD><Badge label={m.isStatic ? 'Built-in' : 'Custom'} color={m.isStatic ? 'var(--ns-text-muted)' : 'oklch(0.72 0.052 198)'} /></TD>
              <TD mono muted>{m.ordinal}</TD>
              <TD>
                <button
                  onClick={() => toggleActive.mutate(m)}
                  disabled={toggleActive.isPending}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  {m.isActive
                    ? <ToggleRight size={18} style={{ color: 'oklch(0.66 0.13 150)' }} />
                    : <ToggleLeft  size={18} style={{ color: 'var(--ns-text-muted)' }} />}
                </button>
              </TD>
              <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => onEdit(m)}><Pencil size={12} /></button>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && <EmptyRow cols={7} msg="No modules match your search" />}
        </tbody>
      </table>
    </div>
  )
}

// ── User drawer (add / edit) ──────────────────────────────────────────────────

function UserDrawer({ open, onClose, subs, user }: {
  open: boolean; onClose: () => void; subs: Subscription[]; user: CUser | null
}) {
  const qc      = useQueryClient()
  const isEdit  = user !== null

  const [subId,       setSubId]       = useState('')
  const [email,       setEmail]       = useState('')
  const [firstName,   setFirstName]   = useState('')
  const [lastName,    setLastName]    = useState('')
  const [displayName, setDisplayName] = useState('')
  const [entraUpn,    setEntraUpn]    = useState('')
  const [errorAlert,  setErrorAlert]  = useState(false)
  const [isActive,    setIsActive]    = useState(true)

  useEffect(() => {
    if (open) {
      setSubId(user?.subscriptionId ?? subs[0]?.id ?? '')
      setEmail(user?.email ?? '')
      setFirstName(user?.firstName ?? '')
      setLastName(user?.lastName ?? '')
      setDisplayName(user?.displayName ?? '')
      setEntraUpn(user?.entraUpn ?? '')
      setErrorAlert(user?.enableErrorAlert ?? false)
      setIsActive(user?.isActive ?? true)
    }
  }, [open, user])

  const create = useMutation({
    mutationFn: () => api.post('/clementine/users', {
      subscriptionId: subId,
      email: email.trim(),
      firstName:   firstName.trim()   || null,
      lastName:    lastName.trim()    || null,
      displayName: displayName.trim() || null,
      entraUpn:    entraUpn.trim()    || null,
      enableErrorAlert: errorAlert,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clementine', 'users'] }); toast.success('User added'); onClose() },
    onError:   () => toast.error('Failed to add user'),
  })

  const update = useMutation({
    mutationFn: () => api.put(`/clementine/users/${user?.id}`, {
      email: email.trim(),
      firstName:   firstName.trim()   || null,
      lastName:    lastName.trim()    || null,
      displayName: displayName.trim() || null,
      entraUpn:    entraUpn.trim()    || null,
      enableErrorAlert: errorAlert,
      isActive,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clementine', 'users'] }); toast.success('User updated'); onClose() },
    onError:   () => toast.error('Failed to update user'),
  })

  const isPending = create.isPending || update.isPending
  const canSave   = email.trim() && (isEdit || subId)
  const autoDisplayName = [firstName, lastName].filter(Boolean).join(' ')

  return (
    <Drawer open={open} onClose={onClose}>
      <div className="drawer-head">
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{isEdit ? 'Edit user' : 'Add user'}</div>
          {isEdit && <div style={{ fontSize: 12, color: 'var(--ns-text-muted)', marginTop: 2 }}>{user?.subscriptionName}</div>}
        </div>
        <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={15} /></button>
      </div>

      <div className="drawer-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {!isEdit && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Subscription
            </label>
            <select className="input" value={subId} onChange={e => setSubId(e.target.value)} style={{ fontFamily: 'inherit' }}>
              <option value="">Select subscription…</option>
              {subs.filter(s => s.isActive).map(s => (
                <option key={s.id} value={s.id}>{s.organizationName}</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Email <span style={{ color: 'var(--ns-err)' }}>*</span>
          </label>
          <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="user@example.com" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>First name</label>
            <input className="input" value={firstName} onChange={e => setFirstName(e.target.value)} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Last name</label>
            <input className="input" value={lastName} onChange={e => setLastName(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Display name</label>
          <input className="input" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder={autoDisplayName || undefined} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Entra UPN</label>
          <input
            className="input"
            value={entraUpn}
            onChange={e => setEntraUpn(e.target.value)}
            placeholder="Microsoft login (if different from email)"
            style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>Error alerts</div>
            <div style={{ fontSize: 11, color: 'var(--ns-text-muted)' }}>Notify on sync errors</div>
          </div>
          <button onClick={() => setErrorAlert(v => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            {errorAlert
              ? <ToggleRight size={22} style={{ color: 'oklch(0.66 0.13 150)' }} />
              : <ToggleLeft  size={22} style={{ color: 'var(--ns-text-muted)' }} />}
          </button>
        </div>

        {isEdit && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--ns-border)' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Active</div>
              <div style={{ fontSize: 11, color: 'var(--ns-text-muted)' }}>Inactive users cannot log in</div>
            </div>
            <button onClick={() => setIsActive(v => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              {isActive
                ? <ToggleRight size={22} style={{ color: 'oklch(0.66 0.13 150)' }} />
                : <ToggleLeft  size={22} style={{ color: 'var(--ns-text-muted)' }} />}
            </button>
          </div>
        )}
      </div>

      <div className="drawer-foot">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button
          className="btn btn-primary"
          disabled={!canSave || isPending}
          onClick={() => isEdit ? update.mutate() : create.mutate()}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add user'}
        </button>
      </div>
    </Drawer>
  )
}

// ── Users tab ─────────────────────────────────────────────────────────────────

function UsersTab({ users, search, onEdit }: { users: CUser[]; search: string; onEdit: (u: CUser) => void }) {
  const qc = useQueryClient()
  const [subFilter, setSubFilter] = useState<string>('all')
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const subsInList = [...new Map(users.map(u => [u.subscriptionId, u.subscriptionName])).entries()]

  const deleteUser = useMutation({
    mutationFn: (id: number) => api.delete(`/clementine/users/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clementine', 'users'] }); setDeletingId(null); toast.success('User removed') },
    onError:   () => toast.error('Failed to remove user'),
  })

  const filtered = users.filter(u =>
    (subFilter === 'all' || u.subscriptionId === subFilter) &&
    ((u.displayName?.toLowerCase().includes(search.toLowerCase()) ?? false) ||
      u.email.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button
          onClick={() => setSubFilter('all')}
          className={subFilter === 'all' ? 'btn-primary' : 'btn-ghost'}
          style={{ fontSize: 12, padding: '3px 10px' }}
        >All</button>
        {subsInList.map(([sid, name]) => (
          <button
            key={sid}
            onClick={() => setSubFilter(sid)}
            className={subFilter === sid ? 'btn-primary' : 'btn-ghost'}
            style={{ fontSize: 12, padding: '3px 10px' }}
          >{name}</button>
        ))}
      </div>
      <div style={{ border: '1px solid var(--ns-border)', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--ns-border)', background: 'var(--ns-surface-2)' }}>
              <TH>Name</TH>
              <TH>Email</TH>
              <TH>Subscription</TH>
              <TH>Error alerts</TH>
              <TH>Status</TH>
              <TH right>Actions</TH>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} style={{ borderTop: '1px solid var(--ns-border)' }} className="table-row-hover">
                <TD><span style={{ fontWeight: 500 }}>{u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email}</span></TD>
                <TD mono muted>{u.email}</TD>
                <TD muted>{u.subscriptionName}</TD>
                <TD>
                  {u.enableErrorAlert
                    ? <Badge label="On"  color="oklch(0.66 0.13 150)" />
                    : <Badge label="Off" color="var(--ns-text-muted)" />}
                </TD>
                <TD>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <StatusDot ok={u.isActive} />
                    <span style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>{u.isActive ? 'Active' : 'Inactive'}</span>
                  </div>
                </TD>
                <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                    <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => onEdit(u)}><Pencil size={12} /></button>
                    {deletingId === u.id ? (
                      <>
                        <span style={{ fontSize: 11, color: 'var(--ns-err)' }}>Remove?</span>
                        <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px', color: 'var(--ns-err)' }} onClick={() => deleteUser.mutate(u.id)} disabled={deleteUser.isPending}>Yes</button>
                        <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => setDeletingId(null)}>No</button>
                      </>
                    ) : (
                      <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px', color: 'var(--ns-err)' }} onClick={() => setDeletingId(u.id)}><Trash2 size={12} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <EmptyRow cols={6} msg="No users found" />}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── DB user drawer (add / edit) ───────────────────────────────────────────────

function DbUserDrawer({ open, onClose, subs, dbUser }: {
  open: boolean; onClose: () => void; subs: Subscription[]; dbUser: DbUser | null
}) {
  const qc     = useQueryClient()
  const isEdit = dbUser !== null

  const [subId,         setSubId]         = useState('')
  const [userType,      setUserType]      = useState<'ReadOnly' | 'StagingEditor'>('ReadOnly')
  const [dbUserId,      setDbUserId]      = useState('')
  const [allowedScopes, setAllowedScopes] = useState('')
  const [allowedIps,    setAllowedIps]    = useState('')
  const [passwordRef,   setPasswordRef]   = useState('')

  useEffect(() => {
    if (open) {
      setSubId(dbUser?.subscriptionId ?? subs[0]?.id ?? '')
      setUserType((dbUser?.userType as 'ReadOnly' | 'StagingEditor') ?? 'ReadOnly')
      setDbUserId(dbUser?.dbUserId ?? '')
      setAllowedScopes(dbUser?.allowedScopes ?? '')
      setAllowedIps(dbUser?.allowedIps ?? '')
      setPasswordRef('')
    }
  }, [open, dbUser])

  const create = useMutation({
    mutationFn: () => api.post('/clementine/database-users', {
      subscriptionId: subId,
      userType,
      dbUserId:      dbUserId.trim()      || null,
      allowedScopes: allowedScopes.trim() || null,
      allowedIps:    allowedIps.trim()    || null,
      dbUserPasswordRef: passwordRef.trim() || null,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clementine', 'database-users'] }); toast.success('DB user added'); onClose() },
    onError:   () => toast.error('Failed to add DB user'),
  })

  const update = useMutation({
    mutationFn: () => api.put(`/clementine/database-users/${dbUser?.id}`, {
      userType,
      dbUserId:      dbUserId.trim()      || null,
      allowedScopes: allowedScopes.trim() || null,
      allowedIps:    allowedIps.trim()    || null,
      dbUserPasswordRef: passwordRef.trim() || null,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clementine', 'database-users'] }); toast.success('DB user updated'); onClose() },
    onError:   () => toast.error('Failed to update DB user'),
  })

  const isPending = create.isPending || update.isPending

  return (
    <Drawer open={open} onClose={onClose}>
      <div className="drawer-head">
        <div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{isEdit ? 'Edit DB user' : 'Add DB user'}</div>
          {isEdit && <div style={{ fontSize: 12, color: 'var(--ns-text-muted)', marginTop: 2 }}>{dbUser?.subscriptionName}</div>}
        </div>
        <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={15} /></button>
      </div>

      <div className="drawer-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {!isEdit && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Subscription <span style={{ color: 'var(--ns-err)' }}>*</span>
            </label>
            <select className="input" value={subId} onChange={e => setSubId(e.target.value)} style={{ fontFamily: 'inherit' }}>
              <option value="">Select subscription…</option>
              {subs.filter(s => s.isActive).map(s => (
                <option key={s.id} value={s.id}>{s.organizationName}</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>User type</label>
          <select className="input" value={userType} onChange={e => setUserType(e.target.value as 'ReadOnly' | 'StagingEditor')} style={{ fontFamily: 'inherit' }}>
            <option value="ReadOnly">ReadOnly</option>
            <option value="StagingEditor">StagingEditor</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Username</label>
          <input className="input" value={dbUserId} onChange={e => setDbUserId(e.target.value)} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Allowed scopes</label>
          <input className="input" value={allowedScopes} onChange={e => setAllowedScopes(e.target.value)} placeholder="e.g. read,write" style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Allowed IPs</label>
          <input className="input" value={allowedIps} onChange={e => setAllowedIps(e.target.value)} placeholder="e.g. 1.2.3.4,5.6.7.0/24" style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ns-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Password (Key Vault ref) {isEdit && <span style={{ fontWeight: 400, textTransform: 'none', color: 'var(--ns-text-muted)' }}>— leave blank to keep existing</span>}
          </label>
          <input className="input" value={passwordRef} onChange={e => setPasswordRef(e.target.value)} placeholder="@Microsoft.KeyVault(…) or secret name" style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }} />
        </div>
      </div>

      <div className="drawer-foot">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button
          className="btn btn-primary"
          disabled={(!isEdit && !subId) || isPending}
          onClick={() => isEdit ? update.mutate() : create.mutate()}
        >
          {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add DB user'}
        </button>
      </div>
    </Drawer>
  )
}

// ── Database users tab ────────────────────────────────────────────────────────

function DatabaseUsersTab({ dbUsers, search, onEdit }: { dbUsers: DbUser[]; search: string; onEdit: (d: DbUser) => void }) {
  const qc = useQueryClient()
  const [showPass,   setShowPass]   = useState<Set<number>>(new Set())
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const filtered = dbUsers.filter(d =>
    d.subscriptionName.toLowerCase().includes(search.toLowerCase()) ||
    (d.dbUserId?.toLowerCase().includes(search.toLowerCase()) ?? false)
  )

  const togglePass = (id: number) => setShowPass(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const deleteDbUser = useMutation({
    mutationFn: (id: number) => api.delete(`/clementine/database-users/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['clementine', 'database-users'] }); setDeletingId(null); toast.success('DB user removed') },
    onError:   () => toast.error('Failed to remove DB user'),
  })

  return (
    <div style={{ border: '1px solid var(--ns-border)', borderRadius: 10, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--ns-border)', background: 'var(--ns-surface-2)' }}>
            <TH>Subscription</TH>
            <TH>Username</TH>
            <TH>Type</TH>
            <TH>Allowed scopes</TH>
            <TH>Allowed IPs</TH>
            <TH>Password</TH>
            <TH right>Actions</TH>
          </tr>
        </thead>
        <tbody>
          {filtered.map(d => (
            <tr key={d.id} style={{ borderTop: '1px solid var(--ns-border)' }} className="table-row-hover">
              <TD>{d.subscriptionName}</TD>
              <TD mono>{d.dbUserId ?? '—'}</TD>
              <TD>
                <Badge
                  label={d.userType}
                  color={d.userType === 'StagingEditor' ? 'oklch(0.75 0.15 75)' : 'var(--ns-text-muted)'}
                />
              </TD>
              <TD mono muted>{d.allowedScopes ?? '—'}</TD>
              <TD mono muted>{d.allowedIps ?? '—'}</TD>
              <TD>
                {d.hasPassword ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                      {showPass.has(d.id) ? '(stored in Key Vault)' : '••••••••'}
                    </span>
                    <button onClick={() => togglePass(d.id)} className="btn-ghost" style={{ padding: '1px 4px' }}>
                      {showPass.has(d.id) ? <EyeOff size={12} /> : <Eye size={12} />}
                    </button>
                  </div>
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>Not set</span>
                )}
              </TD>
              <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                  <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => onEdit(d)}><Pencil size={12} /></button>
                  {deletingId === d.id ? (
                    <>
                      <span style={{ fontSize: 11, color: 'var(--ns-err)' }}>Remove?</span>
                      <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px', color: 'var(--ns-err)' }} onClick={() => deleteDbUser.mutate(d.id)} disabled={deleteDbUser.isPending}>Yes</button>
                      <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => setDeletingId(null)}>No</button>
                    </>
                  ) : (
                    <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 8px', color: 'var(--ns-err)' }} onClick={() => setDeletingId(d.id)}><Trash2 size={12} /></button>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && <EmptyRow cols={7} msg="No database users found" />}
        </tbody>
      </table>
    </div>
  )
}

// ── Sync config tab ────────────────────────────────────────────────────────────

function SyncConfigTab({ configs, search }: { configs: SyncConfig[]; search: string }) {
  const qc = useQueryClient()
  const updateConfig = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { isSyncFromFortnox: boolean; isEnableSync: boolean } }) =>
      api.put(`/clementine/sync-config/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clementine', 'sync-config'] }),
    onError:   () => toast.error('Failed to update sync config'),
  })

  const filtered = configs.filter(c =>
    c.subscriptionName.toLowerCase().includes(search.toLowerCase()) ||
    c.featureName.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div style={{ border: '1px solid var(--ns-border)', borderRadius: 10, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--ns-border)', background: 'var(--ns-surface-2)' }}>
            <TH>Subscription</TH>
            <TH>Company</TH>
            <TH>Feature</TH>
            <TH>From Fortnox</TH>
            <TH>Sync enabled</TH>
          </tr>
        </thead>
        <tbody>
          {filtered.map(c => (
            <tr key={c.id} style={{ borderTop: '1px solid var(--ns-border)' }} className="table-row-hover">
              <TD>{c.subscriptionName}</TD>
              <TD muted>{c.companyName ?? '—'}</TD>
              <TD mono>{c.featureName}</TD>
              <TD>
                <button
                  onClick={() => updateConfig.mutate({ id: c.id, patch: { isSyncFromFortnox: !c.isSyncFromFortnox, isEnableSync: c.isEnableSync } })}
                  disabled={updateConfig.isPending}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  {c.isSyncFromFortnox
                    ? <ToggleRight size={18} style={{ color: 'oklch(0.66 0.13 150)' }} />
                    : <ToggleLeft  size={18} style={{ color: 'var(--ns-text-muted)' }} />}
                </button>
              </TD>
              <TD>
                <button
                  onClick={() => updateConfig.mutate({ id: c.id, patch: { isSyncFromFortnox: c.isSyncFromFortnox, isEnableSync: !c.isEnableSync } })}
                  disabled={updateConfig.isPending}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  {c.isEnableSync
                    ? <ToggleRight size={18} style={{ color: 'oklch(0.66 0.13 150)' }} />
                    : <ToggleLeft  size={18} style={{ color: 'var(--ns-text-muted)' }} />}
                </button>
              </TD>
            </tr>
          ))}
          {filtered.length === 0 && <EmptyRow cols={5} msg="No sync config entries found" />}
        </tbody>
      </table>
    </div>
  )
}

// ── Sync log tab ──────────────────────────────────────────────────────────────

function SyncLogTab({ log, search }: { log: SyncEntry[]; search: string }) {
  const [errOnly, setErrOnly] = useState(false)

  const filtered = log.filter(l =>
    (!errOnly || l.hasError) &&
    (l.subscriptionName.toLowerCase().includes(search.toLowerCase()) ||
     (l.companyName?.toLowerCase().includes(search.toLowerCase()) ?? false))
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => setErrOnly(v => !v)}
          className={errOnly ? 'btn-primary' : 'btn-ghost'}
          style={{ fontSize: 12, padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 5 }}
        >
          <AlertTriangle size={12} /> Errors only
        </button>
      </div>
      <div style={{ border: '1px solid var(--ns-border)', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--ns-border)', background: 'var(--ns-surface-2)' }}>
              <TH>Subscription</TH>
              <TH>Company</TH>
              <TH>Source</TH>
              <TH>Started</TH>
              <TH>Duration</TH>
              <TH>Status</TH>
              <TH>Tries</TH>
            </tr>
          </thead>
          <tbody>
            {filtered.map(l => {
              const dur = l.startedAt && l.completedAt
                ? `${Math.round((new Date(l.completedAt).getTime() - new Date(l.startedAt).getTime()) / 1000)}s`
                : '—'
              return (
                <tr key={l.id} style={{ borderTop: '1px solid var(--ns-border)' }} className="table-row-hover">
                  <TD>{l.subscriptionName}</TD>
                  <TD muted>{l.companyName ?? '—'}</TD>
                  <TD><Badge label={l.source} color='oklch(0.72 0.052 198)' /></TD>
                  <TD muted>{relTime(l.startedAt)}</TD>
                  <TD mono muted>{dur}</TD>
                  <TD>
                    {l.hasError ? (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <XCircle size={13} style={{ color: 'var(--ns-err)' }} />
                          <span style={{ fontSize: 12, color: 'var(--ns-err)' }}>Error</span>
                        </div>
                        {l.errorMessage && (
                          <div style={{ fontSize: 11, color: 'var(--ns-text-muted)', marginTop: 2, maxWidth: 280 }}>
                            {l.errorMessage}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <CheckCircle2 size={13} style={{ color: 'oklch(0.66 0.13 150)' }} />
                        <span style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>{l.runStatus ?? 'OK'}</span>
                      </div>
                    )}
                  </TD>
                  <TD mono muted>{l.tryCount}</TD>
                </tr>
              )
            })}
            {filtered.length === 0 && <EmptyRow cols={7} msg="No log entries found" />}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function CompaniesTab({ companies, search }: { companies: Company[]; search: string }) {
  const [activeOnly, setActiveOnly] = useState(false)

  const filtered = companies.filter(c => {
    if (activeOnly && !c.isActive) return false
    const q = search.toLowerCase()
    return (
      c.name.toLowerCase().includes(q) ||
      (c.organizationNumber?.toLowerCase().includes(q) ?? false) ||
      c.subscriptionName.toLowerCase().includes(q)
    )
  })

  const enabledCount  = companies.filter(c => c.isActive).length
  const disabledCount = companies.length - enabledCount

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          onClick={() => setActiveOnly(v => !v)}
          className={activeOnly ? 'btn-primary' : 'btn-ghost'}
          style={{ fontSize: 12, padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 5 }}
        >
          <CheckCircle2 size={12} /> Enabled only
        </button>
        <span style={{ fontSize: 12, color: 'var(--ns-text-muted)', marginLeft: 4 }}>
          {enabledCount} enabled · {disabledCount} disabled
        </span>
      </div>
      <div style={{ border: '1px solid var(--ns-border)', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--ns-border)', background: 'var(--ns-surface-2)' }}>
              <TH>Customer</TH>
              <TH>Company</TH>
              <TH>Org number</TH>
              <TH>Sync enabled</TH>
              <TH>Active modules</TH>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => {
              const mods = c.modules
              return (
                <tr key={c.id} style={{ borderTop: '1px solid var(--ns-border)' }} className="table-row-hover">
                  <TD muted>{c.subscriptionName}</TD>
                  <TD>{c.name}</TD>
                  <TD mono muted>{c.organizationNumber ?? '—'}</TD>
                  <TD>
                    {c.isActive ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <CheckCircle2 size={13} style={{ color: 'oklch(0.66 0.13 150)' }} />
                        <span style={{ fontSize: 12, color: 'oklch(0.66 0.13 150)' }}>Enabled</span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <XCircle size={13} style={{ color: 'var(--ns-text-muted)' }} />
                        <span style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>Disabled</span>
                      </div>
                    )}
                  </TD>
                  <TD>
                    {mods.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {mods.map(m => (
                          <span key={m} style={{
                            fontSize: 11, fontWeight: 500, padding: '2px 7px', borderRadius: 4,
                            background: 'oklch(0.66 0.13 150 / 0.12)',
                            color: 'oklch(0.55 0.13 150)',
                            border: '1px solid oklch(0.66 0.13 150 / 0.25)',
                          }}>{m}</span>
                        ))}
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--ns-text-muted)' }}>—</span>
                    )}
                  </TD>
                </tr>
              )
            })}
            {filtered.length === 0 && <EmptyRow cols={5} msg="No companies found" />}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function ClementinePage() {
  const [tab, setTab]               = useState<Tab>('subscriptions')
  const [search, setSearch]         = useState('')
  const [onboarding, setOnboarding] = useState(false)
  const [editingSub,    setEditingSub]    = useState<Subscription | null>(null)
  const [addingModule,  setAddingModule]  = useState(false)
  const [editingModule, setEditingModule] = useState<CModule | null>(null)
  const [addingUser,    setAddingUser]    = useState(false)
  const [editingUser,   setEditingUser]   = useState<CUser | null>(null)
  const [addingDbUser,  setAddingDbUser]  = useState(false)
  const [editingDbUser, setEditingDbUser] = useState<DbUser | null>(null)

  const { data: subs    = [] } = useQuery({ queryKey: ['clementine', 'subscriptions'],  queryFn: () => api.get<Subscription[]>('/clementine/subscriptions') })
  const { data: modules = [] } = useQuery({ queryKey: ['clementine', 'modules'],        queryFn: () => api.get<CModule[]>('/clementine/modules') })
  const { data: users   = [] } = useQuery({ queryKey: ['clementine', 'users'],          queryFn: () => api.get<CUser[]>('/clementine/users') })
  const { data: dbUsers = [] } = useQuery({ queryKey: ['clementine', 'database-users'], queryFn: () => api.get<DbUser[]>('/clementine/database-users') })
  const { data: syncCfg = [] } = useQuery({ queryKey: ['clementine', 'sync-config'],    queryFn: () => api.get<SyncConfig[]>('/clementine/sync-config') })
  const { data: syncLog    = [], refetch: refetchLog } = useQuery({ queryKey: ['clementine', 'sync-log'],   queryFn: () => api.get<SyncEntry[]>('/clementine/sync-log') })
  const { data: companies  = [] }                     = useQuery({ queryKey: ['clementine', 'companies'],  queryFn: () => api.get<Company[]>('/clementine/companies') })

  const errorCount = syncLog.filter(l => l.hasError).length
  const activeSubs = subs.filter(s => s.isActive).length

  const onTabChange = (t: Tab) => { setTab(t); setSearch('') }

  const addLabel: Record<Tab, string> = {
    'subscriptions':  'Add subscription',
    'modules':        'Add module',
    'users':          'Add user',
    'database-users': 'Add DB user',
    'companies':      '',
    'sync-config':    'Add config',
    'sync-log':       '',
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Clementine Host</h1>
          <p style={{ fontSize: 13, color: 'var(--ns-text-muted)', margin: '3px 0 0' }}>
            Manage tenant subscriptions, modules, and sync health
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            className="btn-ghost"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
            onClick={() => refetchLog()}
            title="Refresh sync log"
          >
            <RefreshCw size={14} />
          </button>
          {addLabel[tab] && (
            <button
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
              onClick={() => {
                if (tab === 'subscriptions')  setOnboarding(true)
                if (tab === 'modules')        setAddingModule(true)
                if (tab === 'users')          setAddingUser(true)
                if (tab === 'database-users') setAddingDbUser(true)
              }}
            >
              <Plus size={14} /> {addLabel[tab]}
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {[
          { label: 'Active subscriptions', value: activeSubs,                                   mono: true  },
          { label: 'Total companies',      value: subs.reduce((n, s) => n + s.companyCount, 0), mono: true  },
          { label: 'Sync errors',          value: errorCount, color: errorCount > 0 ? 'var(--ns-err)' : 'oklch(0.66 0.13 150)' },
          { label: 'Modules registered',   value: modules.filter(m => m.isActive).length,       mono: true  },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--ns-surface)', border: '1px solid var(--ns-border)', borderRadius: 10, padding: '12px 16px' }}>
            <div style={{ fontSize: 11, color: 'var(--ns-text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
              {s.label}
            </div>
            <div style={{ fontSize: 24, fontWeight: 600, fontFamily: s.mono ? 'var(--font-mono)' : undefined, color: s.color ?? 'var(--ns-text)' }}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }} role="tablist">
        {([
          ['subscriptions',  'Subscriptions',  Building2, subs.length    ],
          ['modules',        'Modules',         Layers,    modules.length  ],
          ['users',          'Users',           Users,     users.length    ],
          ['database-users', 'Database Users',  Database,   dbUsers.length    ],
          ['companies',      'Companies',       Building2,  companies.length  ],
          ['sync-config',    'Sync Config',     Settings2,  syncCfg.length    ],
          ['sync-log',       'Sync Log',        ScrollText, errorCount > 0 ? errorCount : undefined],
        ] as const).map(([t, label, Icon, count]) => (
          <button
            key={t}
            onClick={() => onTabChange(t as Tab)}
            style={{ all: 'unset', cursor: 'pointer', display: 'contents' }}
          >
            <TabBtn tab={t as Tab} active={tab} label={label} icon={Icon} count={count as number | undefined} />
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ position: 'relative', maxWidth: 320 }}>
        <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ns-text-muted)' }} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={`Search ${tab.replace('-', ' ')}…`}
          style={{ width: '100%', paddingLeft: 32, paddingRight: 12, height: 32, fontSize: 13 }}
          className="input"
        />
      </div>

      {/* Content */}
      {tab === 'subscriptions'  && <SubscriptionsTab subs={subs}       search={search} modules={modules} onEdit={s => setEditingSub(s)} />}
      {tab === 'modules'        && <ModulesTab       modules={modules}  search={search} onEdit={m => setEditingModule(m)} />}
      {tab === 'users'          && <UsersTab         users={users}      search={search} onEdit={u => setEditingUser(u)} />}
      {tab === 'database-users' && <DatabaseUsersTab dbUsers={dbUsers}    search={search} onEdit={d => setEditingDbUser(d)} />}
      {tab === 'companies'      && <CompaniesTab     companies={companies} search={search} />}
      {tab === 'sync-config'    && <SyncConfigTab    configs={syncCfg}    search={search} />}
      {tab === 'sync-log'       && <SyncLogTab       log={syncLog}      search={search} />}

      <OnboardDrawer   open={onboarding}    onClose={() => setOnboarding(false)} />
      <SubEditDrawer   open={editingSub !== null}    onClose={() => setEditingSub(null)}    sub={editingSub}  allModules={modules} />
      <ModuleDrawer    open={addingModule || editingModule !== null} onClose={() => { setAddingModule(false); setEditingModule(null) }} module={editingModule} />
      <UserDrawer      open={addingUser  || editingUser  !== null}  onClose={() => { setAddingUser(false);  setEditingUser(null)  }} subs={subs} user={editingUser} />
      <DbUserDrawer    open={addingDbUser || editingDbUser !== null} onClose={() => { setAddingDbUser(false); setEditingDbUser(null) }} subs={subs} dbUser={editingDbUser} />

    </div>
  )
}
